/**
 * useIdentityContexts — headless resolver for "which surfaces can this account reach".
 *
 * SECURITY: every context returned here is derived from server-resolved rows —
 * `user_roles` for auth.uid() (via adminService.checkUserRoles, which is
 * RLS-scoped to the caller) and `list_accessible_tenants_for_user` for tenant
 * labels. There is no client-supplied role flag anywhere in this file, so the
 * switcher physically cannot offer a context the account does not hold. The
 * real boundary remains ProtectedRoute + RLS; this hook only decides what is
 * worth *showing*.
 */
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { adminService, tenantService } from "@/services";

export type IdentityContextKind = "consumer" | "creator" | "tenant" | "platform";

export interface IdentityContext {
  kind: IdentityContextKind;
  /** tenant id for kind === "tenant", otherwise the kind itself. */
  id: string;
  label: string;
  /** Short qualifier shown under the label so near-identical names can't be confused. */
  caption?: string;
  path: string;
}

export interface UseIdentityContexts {
  contexts: IdentityContext[];
  loading: boolean;
  activeId: string | null;
  /** Switch context; for tenants this also sets adminTenantId so admin surfaces follow. */
  select: (ctx: IdentityContext) => void;
  refresh: () => void;
}

export function useIdentityContexts(): UseIdentityContexts {
  const { user, adminTenantId, setAdminTenantId } = useAuth();
  const [contexts, setContexts] = useState<IdentityContext[]>([]);
  const [loading, setLoading] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setContexts([]);
      return;
    }
    setLoading(true);
    (async () => {
      const list: IdentityContext[] = [
        { kind: "consumer", id: "consumer", label: "Your cards", path: "/account" },
        { kind: "creator", id: "creator", label: "Sharing & earnings", path: "/creator" },
      ];

      const { data: roles } = await adminService.checkUserRoles(user.id);
      const rows = (roles ?? []) as { role: string; tenant_id: string | null }[];
      const isSuper = rows.some((r) => r.role === "super_admin");
      const staffRows = rows.filter((r) => r.role === "admin" || r.role === "approver" || r.role === "super_admin");

      if (isSuper) {
        // The PLATFORM surface is not a tenant. It is deliberately labelled
        // differently from the "Cardmoji Platform" TENANT (the interim
        // super_admin anchor), which sits in the Tenants group below and lands
        // on the tenant-admin sidebar. Same name, different surface — the
        // captions are what keep them apart.
        list.push({
          kind: "platform",
          id: "platform",
          label: "Platform console",
          caption: "All tenants · super admin",
          path: "/platform/analytics",
        });
      }

      if (staffRows.length > 0) {
        try {
          if (isSuper) {
            const { data } = await tenantService.listTenantSummaries();
            for (const t of (data ?? []) as { id: string; name: string }[]) {
              list.push({ kind: "tenant", id: t.id, label: t.name, caption: "Tenant admin", path: "/admin/dashboard" });
            }
          } else {
            const { data } = await tenantService.listAccessibleTenantsForUser(user.id);
            for (const t of (data ?? []) as { id: string; name: string }[]) {
              list.push({ kind: "tenant", id: t.id, label: t.name, caption: "Tenant admin", path: "/admin/dashboard" });
            }
          }
        } catch {
          /* label lookup failure must not invent contexts */
        }
      }

      if (!cancelled) {
        setContexts(list);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, nonce]);

  const select = useCallback(
    (ctx: IdentityContext) => {
      if (ctx.kind === "tenant") setAdminTenantId(ctx.id);
    },
    [setAdminTenantId]
  );

  const activeId = adminTenantId ?? null;

  return {
    contexts,
    loading,
    activeId,
    select,
    refresh: useCallback(() => setNonce((n) => n + 1), []),
  };
}
